import { ArrowRightLeft, Ban, RefreshCw } from 'lucide-react';
import { formatDate, formatRelative } from '@/lib/format';
import { t } from '@/lib/i18n';
import { EntityLink, IconButton, Pill } from '@/ui';
import type { DataColumn } from '@/ui/DataTable';
import s from '../Admin.module.css';
import { msUntil } from '../components/forms';
import { scopeLabel } from '../labels';
import type { ApiKeyOut } from '../types';

const DAY = 86_400_000;

function Status({ k }: { k: ApiKeyOut }) {
  if (k.status === 'revoked') return <Pill tone="neutral">{t('Revoked')}</Pill>;
  if (k.status === 'expired') return <Pill tone="bad">{t('Expired')}</Pill>;
  if (k.expires_at && msUntil(k.expires_at) < 30 * DAY) return <Pill tone="caution">{t('Expires soon')}</Pill>;
  return <Pill tone="good">{t('Active')}</Pill>;
}

export type KeyAct = 'rotate' | 'revoke' | 'transfer';

export function keyColumns(act: (k: ApiKeyOut, what: KeyAct) => void): DataColumn<ApiKeyOut>[] {
  return [
    { id: 'name', header: t('Name'), primary: true, width: '200px', accessor: (k) => k.name, cell: (k) => <span className={s.strong}>{k.name}</span> },
    { id: 'prefix', header: t('Starts with'), width: '140px', accessor: (k) => k.prefix, cell: (k) => <span className={s.mono}>{k.prefix}…</span> },
    { id: 'status', header: t('Status'), secondary: true, width: '130px', accessor: (k) => k.status, cell: (k) => <Status k={k} /> },
    {
      id: 'role',
      header: t('Role'),
      width: '170px',
      accessor: (k) => k.role?.name ?? '',
      cell: (k) =>
        k.role ? (
          <EntityLink type="role" id={k.role.id}>
            {k.role.name}
          </EntityLink>
        ) : (
          '—'
        ),
    },
    {
      id: 'scope',
      header: t('Where'),
      width: '150px',
      accessor: (k) => (k.scope_ids.length ? `${scopeLabel(k.scope)} (${k.scope_ids.length})` : scopeLabel(k.scope)),
    },
    {
      id: 'last_used',
      header: t('Last used'),
      width: '130px',
      accessor: (k) => k.last_used_at,
      cell: (k) => (k.last_used_at ? formatRelative(k.last_used_at) : <span className={s.muted}>{t('Never')}</span>),
    },
    { id: 'expires', header: t('Expires'), width: '130px', accessor: (k) => k.expires_at, cell: (k) => (k.expires_at ? formatDate(k.expires_at) : t('Never')) },
    // the key's owner: its creator, or whoever it was handed to (a key holds only what its owner holds)
    { id: 'created_by', header: t('Owner'), width: '150px', accessor: (k) => k.created_by?.label ?? '', cell: (k) => k.created_by?.label || '—' },
    { id: 'created', header: t('Created'), width: '120px', accessor: (k) => k.created_at, cell: (k) => formatDate(k.created_at) },
    {
      id: 'actions',
      header: t('Actions'),
      width: '128px',
      sortable: false,
      hideable: false,
      csv: false,
      cell: (k) =>
        k.status === 'active' ? (
          <span className={s.row}>
            <IconButton icon={RefreshCw} size="sm" label={t('Rotate {name}', { name: k.name })} onClick={() => act(k, 'rotate')} />
            <IconButton icon={ArrowRightLeft} size="sm" label={t('Hand over {name}', { name: k.name })} onClick={() => act(k, 'transfer')} />
            <IconButton icon={Ban} size="sm" label={t('Revoke {name}', { name: k.name })} onClick={() => act(k, 'revoke')} />
          </span>
        ) : null,
    },
  ];
}
